// The communication host — `comm.symbols.app` (prod) and
// `<tier>.comm.symbols.app` (`dev.comm.symbols.app`, …). It serves the SAME
// shell build from the SAME bucket as the workspace host of its tier
// (`my.symbols.app`, `dev.my.symbols.app`), so it is always the current build.
// Only the profile differs: chat, calls, inbox, calendar, mail, reminders,
// people, agents, the AI composer and Fly (commProfileData.js). Contract:
// workspace docs/COMM_HOST.md.
//
// The pairing is ONE label rule, no channel list: the `comm` label right
// before `symbols.app` ↔ the `my` label. `comm.symbols.app` ↔ `my.symbols.app`,
// `dev.comm.symbols.app` ↔ `dev.my.symbols.app`.
//
// THE LEGACY `com` LABEL (Nika 2026-10-24: "change domain to com.symbols to
// comm.symbols"): `com.symbols.app` was the comm host's first name. Every
// test here still accepts it (`comm?`), so a build reaching a tier works on
// both names in any order; each tier's old host then redirects to the new
// one at the edge (docs/COMM_HOST.md). The pairing writes `comm` only.
//
// FA204 / frank trap — every export is self-contained (the label rule is
// restated where it is used); the lists come from commProfileData.js, a
// cross-file import, which resolves through frank's globalScope fallback.
//
// Local simulation (no DNS): `?comm=1` (one visit) or
// `localStorage.symbols_force_comm='1'` — the demoHost.js convention. A
// simulated comm host has no workspace host to send other routes to, so they
// land on the comm home instead (commRouteGuard).
import { commRoutes, commHomePath, commAssistantModes } from './commProfileData.js'
import { stripWorkspacePrefix } from './workspacePrefix.js'

// Is this hostname a comm host? Pure.
export const isCommHostname = (hostname) => {
  const h = typeof hostname === 'string' ? hostname.split(':')[0].trim().toLowerCase() : ''
  return /(^|\.)comm?\.symbols\.app$/.test(h)
}

// The workspace host paired with a comm host, or null. Pure.
export const myHostForComm = (hostname) => {
  const h = typeof hostname === 'string' ? hostname.split(':')[0].trim().toLowerCase() : ''
  const m = /^((?:[a-z0-9-]+\.)*)comm?\.symbols\.app$/.exec(h)
  return m ? `${m[1]}my.symbols.app` : null
}

// The comm host paired with a workspace host, or null. Pure.
export const commHostForMy = (hostname) => {
  const h = typeof hostname === 'string' ? hostname.split(':')[0].trim().toLowerCase() : ''
  const m = /^((?:[a-z0-9-]+\.)*)my\.symbols\.app$/.exec(h)
  return m ? `${m[1]}comm.symbols.app` : null
}

// Does this page run the comm profile? SSR-safe, never throws.
export const isCommHost = () => {
  if (typeof window === 'undefined') return false
  try {
    const host = String((window.location && window.location.hostname) || '').toLowerCase()
    if (/(^|\.)comm?\.symbols\.app$/.test(host)) return true
    if (window.localStorage.getItem('symbols_force_comm') === '1') return true
    if ((window.location.search || '').includes('comm=1')) return true
  } catch (_) {
    // Sandboxed storage / location — the workspace profile.
  }
  return false
}

// Does the comm host serve this path? `/w/<org>` is stripped first, and the
// bare home is not served (it lands on commHomePath()).
export const isCommRoute = (pathname) => {
  const p = stripWorkspacePrefix(String(pathname || '').split(/[?#]/)[0]) || '/'
  if (p === '/') return false
  return commRoutes().some((base) => p === base || p.indexOf(base + '/') === 0)
}

// el.call('commHome') — where the comm host lands.
export const commHome = () => commHomePath()

// The AI mode this host SHOWS for a stored one. A comm host offers
// commAssistantModes(); any other stored mode (the account's choice, made on
// the workspace host) shows as 'assistant' here and is never written back.
// Everywhere else the mode passes through.
export const commAssistantMode = (mode) => {
  let comm = false
  try {
    const host = String((window.location && window.location.hostname) || '').toLowerCase()
    comm =
      /(^|\.)comm?\.symbols\.app$/.test(host) ||
      window.localStorage.getItem('symbols_force_comm') === '1' ||
      (window.location.search || '').includes('comm=1')
  } catch (_) {
    comm = false
  }
  if (!comm) return mode
  return commAssistantModes().includes(mode) ? mode : 'assistant'
}

// The full URL of `path` on the workspace host paired with this comm host,
// or null when the page is not on a real comm host (a simulated one).
export const workspaceHostUrl = (path) => {
  if (typeof window === 'undefined' || !window.location) return null
  const host = String(window.location.hostname || '').toLowerCase()
  const m = /^((?:[a-z0-9-]+\.)*)comm?\.symbols\.app$/.exec(host)
  if (!m) return null
  const p = typeof path === 'string' && path.startsWith('/') ? path : '/'
  return `https://${m[1]}my.symbols.app${p}`
}

// THE one sanctioned raw-location write of the comm profile: open `path` on
// the workspace host. A cross-origin page load cannot go through el.router
// (the SPA router only knows this origin) — the same reason
// openNotificationTarget.js hard-loads a cross-org href. Window resolves
// `el.context.window`-first (mwtRouter's `_win` idiom, the tests' seam).
// Returns false when there is no workspace host (a simulated comm host) or no
// window, so the caller keeps its SPA fallback.
// After a workspace or org switch the comm app (workspace comm/app.js) starts
// over on its home — a full load, like a fresh visit: every page reads the
// new scope from the start and nothing of the old one stays in memory. The
// comm profile's other sanctioned raw-location write (openOnWorkspaceHost
// below); the same `el.context.window`-first seam.
export const reloadCommHome = (el) => {
  const win =
    (el && el.context && el.context.window) || (typeof window !== 'undefined' ? window : null)
  if (!win || !win.location || typeof win.location.assign !== 'function') return false
  try {
    win.location.assign(commHomePath())
    return true
  } catch (_) {
    return false
  }
}

export const openOnWorkspaceHost = (el, path) => {
  const win =
    (el && el.context && el.context.window) || (typeof window !== 'undefined' ? window : null)
  if (!win || !win.location || typeof win.location.assign !== 'function') return false
  const host = String(win.location.hostname || '').toLowerCase()
  const m = /^((?:[a-z0-9-]+\.)*)comm?\.symbols\.app$/.exec(host)
  if (!m) return false
  const p = typeof path === 'string' && path.startsWith('/') ? path : '/'
  try {
    win.location.assign(`https://${m[1]}my.symbols.app${p}`)
    return true
  } catch (_) {
    return false
  }
}
